import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from '@phosphor-icons/react';

// Phase 5 — Staff Dashboard widget. Shown at the top of /my-work for non-admins:
// today status, pending work, monthly attendance, leave balance, salary estimate,
// personal performance score.

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const STATUS_LABEL = {
  present: 'Present', half_day: 'Half Day', absent: 'Absent', weekly_off: 'Weekly Off', leave: 'On Leave', not_marked: 'Not Marked',
};
const STATUS_COLOR = {
  present: 'bg-[#DCFCE7] text-[#15803D]', half_day: 'bg-[#FFEDD5] text-[#9A3412]', absent: 'bg-[#FEE2E2] text-[#B91C1C]',
  weekly_off: 'bg-[#E0E7FF] text-[#4338CA]', leave: 'bg-[#EDE9FE] text-[#6D28D9]', not_marked: 'bg-[#F4F4F5] text-[#52525B]',
};
const BUCKET = {
  excellent: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]',
  good: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]',
  average: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
  needs_improvement: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]',
};

export default function MyPulseWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/staff-dashboard/me').then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const m = data.month;
  const t = data.today;
  const ms = data.month_summary || {};
  const lb = data.leave_balance || {};
  const se = data.salary_estimate || {};
  const perf = data.performance || {};
  const todayStatus = t?.status || 'not_marked';

  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="my-pulse-widget">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">My Day · {m}</p>
          <Link to="/staff/attendance" className="text-[10px] text-[#3B82F6] hover:underline flex items-center gap-1">Attendance <ArrowRight size={10} /></Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Today</p>
            <Badge className={`rounded-md text-[10px] ${STATUS_COLOR[todayStatus] || STATUS_COLOR.not_marked}`} variant="outline">{STATUS_LABEL[todayStatus] || todayStatus}</Badge>
          </div>
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Pending Work</p>
            <p className="text-xl font-bold font-mono text-[#0A0A0A]">{data.pending_work || 0}</p>
          </div>
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Working Days</p>
            <p className="text-xl font-bold font-mono text-[#0A0A0A]">{ms.present + (ms.half_day || 0) * 0.5}/{data.working_days}</p>
          </div>
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Late Entries</p>
            <p className={`text-xl font-bold font-mono ${ms.late > 3 ? 'text-[#B91C1C]' : 'text-[#0A0A0A]'}`}>{ms.late || 0}</p>
          </div>
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Leave Balance</p>
            <p className="text-xl font-bold font-mono text-[#10B981]">{lb.paid_remaining || 0}<span className="text-[10px] text-[#A1A1AA] font-normal">/{lb.paid_limit || 0}</span></p>
          </div>
          <div className="border border-[#E4E4E7] rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Performance</p>
            <Badge className={`rounded-md text-[10px] ${BUCKET[perf.bucket] || BUCKET.average}`} variant="outline">{perf.score ?? '—'}</Badge>
          </div>
        </div>

        <div className="bg-[#F8FAFC] border border-[#E4E4E7] rounded-lg p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Salary Estimate ({m})</p>
            <p className="text-[10px] text-[#A1A1AA]">Basic {fmtINR(se.basic)} − Auto deductions {fmtINR(se.auto_deductions)} (final slip will include incentives/advance)</p>
          </div>
          <p className={`text-2xl font-bold font-mono ${se.net_estimate < 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmtINR(se.net_estimate)}</p>
        </div>
      </CardContent>
    </Card>
  );
}
